import { State } from '../State';
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import features from '../ruleset/features';
import { assertNever } from '../utils';
import { duration } from '../utils/duration';
import {
  getJSTDate,
  getJSTDayOfTheWeek,
  setJSTHours,
  sortedDaysOfTheWeek,
} from '../utils/time';
import {
  getTeamChestEventStatus,
  getTeamChestRewards,
} from './chest/teamChest.getters';
import { getTeamCompetitionEventStatus } from './competition/teamCompetition.getters';
import {
  TEAM_COMPETITION_REWARDS,
  TeamEventData,
} from './competition/teamCompetition.ruleset';
import { TeamEventType, teamCompetitionSchedule } from './team.ruleset';
import { TeamState } from './team.schema';

/**
 * Get current active event schedule.
 */
export function getCurrentTeamEventSchedule(now: number) {
  const day = getJSTDayOfTheWeek(now);

  // Find current event.
  const todayIndex = sortedDaysOfTheWeek.indexOf(day);
  const currentEvent = teamCompetitionSchedule.find((schedule) => {
    // Check if today is within event days.
    const start = sortedDaysOfTheWeek.indexOf(schedule.start.day);
    const end = sortedDaysOfTheWeek.indexOf(schedule.end.day);

    let matchesDay = false;
    if (start <= end) {
      matchesDay = start <= todayIndex && todayIndex <= end;
    } else {
      matchesDay = start <= todayIndex || todayIndex <= end;
    }

    // No event today.
    if (!matchesDay) return false;

    // Check if current time is within event hours only if it's start or end day.
    if (start === todayIndex) {
      const startHour = schedule.start.hour;
      const hour = getJSTDate(now).getHours();
      return hour >= startHour;
    }
    if (end === todayIndex) {
      const endHour = schedule.end.hour;
      const hour = getJSTDate(now).getHours();
      return hour < endHour;
    }

    return true;
  });

  return currentEvent;
}

/**
 * Get current active team event id.
 */
export function getCurrentTeamEventId(now: number): string | undefined {
  // Get current event schedule.
  const currentEvent = getCurrentTeamEventSchedule(now);

  // No event today. Bail.
  if (!currentEvent) return undefined;

  // Set date day to start of the event.
  const day = getJSTDayOfTheWeek(now);
  const todayIndex = sortedDaysOfTheWeek.indexOf(day);
  const date = getJSTDate(now);

  let offset = sortedDaysOfTheWeek.indexOf(currentEvent.start.day) - todayIndex;
  if (offset > 0) offset -= 7;
  date.setDate(date.getDate() + offset);

  // Return event id.
  return (
    // We cannot simply convert to ISO here, since the date would be wrong.
    // So we convert to an ISO-like date format and build the ISO string ourselves.
    date
      .toLocaleDateString('ja-JA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\//gi, '-') + `T${currentEvent.start.hour}:00:00.000+0900`
  );
}

export function getCurrentTeamEventType(
  now: number,
): TeamEventType | undefined {
  const currentEvent = getCurrentTeamEventSchedule(now);

  // No event today. Bail.
  if (!currentEvent) return undefined;

  return currentEvent.type;
}

function getCurrentTeamEventStartTime(now: number) {
  const eventId = getCurrentTeamEventId(now);
  if (!eventId) return undefined;

  return getTeamEventStartTime(eventId);
}

export function getTeamEventStartTime(eventId: string): Date {
  return new Date(`${eventId}`);
}

/**
 * Get current team event expiration date.
 * @param now
 */
export function getCurrentTeamEventEndDate(now: number): number | undefined {
  // Get current event schedule.
  const currentEvent = getCurrentTeamEventSchedule(now);
  if (!currentEvent) {
    return undefined;
  }

  const date = getCurrentTeamEventStartTime(now)!;
  const day = getJSTDayOfTheWeek(date.getTime());
  const todayIndex = sortedDaysOfTheWeek.indexOf(day);
  const endDay = sortedDaysOfTheWeek.indexOf(currentEvent.end.day);
  let dayDiff = endDay - todayIndex;
  if (dayDiff < 0) dayDiff += 7;

  date.setTime(date.getTime() + duration({ days: dayDiff }));
  setJSTHours(date, currentEvent.end.hour, 0, 0, 0);

  return date.getTime() - 1;
}

/**
 * Get event end date.
 * @param eventId
 */
export function getTeamEventEndDate(eventId: string): number | undefined {
  const now = getTeamEventStartTime(eventId).getTime();
  return getCurrentTeamEventEndDate(now);
}

/**
 * Get previous team event id.
 * @param now
 */
export function getPreviousTeamEventId(
  teamState: TeamState,
  now: number,
): string | undefined {
  // Sort events by date.
  const previousEvent = Object.keys(teamState.global.events)
    .filter((eventId) => getCurrentTeamEventId(now) !== eventId)
    .sort(
      (a, b) =>
        teamState.global.events[b].createdAt -
        teamState.global.events[a].createdAt,
    )[0];

  // Bail if no previous event.
  if (!previousEvent) return undefined;

  // Discart if event is older than a week.
  const age = now - teamState.global.events[previousEvent].createdAt;
  if (age > duration({ days: 7 })) return undefined;

  // Return event id.
  return previousEvent;
}

/**
 * Chek if user is in a team.
 * @param user
 */
export function isUserInTeam(user: State): boolean {
  return !!user.team.currentTeamId;
}

/**
 * Check which team bracket the team is in.
 * @param state
 * @param now
 */
export function getTeamBracket(
  state: TeamState,
  now: number,
): 'active' | 'new' | 'lowActivity' {
  const previousEventId = getPreviousTeamEventId(state, now);
  const score = getTeamEventScore(state, previousEventId);

  if (score > 3 * Object.keys(state.global.members).length) {
    return 'active';
  }
  if (score > 20) {
    return 'lowActivity';
  }
  return 'new';
}

/**
 * Get event score for a team.
 * @param state
 * @param eventId
 */
export function getTeamEventScore(
  state: TeamState,
  eventId: string | undefined,
): number {
  // Bail if event id is undefined.
  if (!eventId) return 0;

  return state.global.events[eventId]?.score ?? 0;
}

/**
 * Get weekly helps for a team.
 * @param state
 * @param eventId
 */
export function getTeamWeeklyHelps(state: TeamState, now: number): number {
  let totalHelps = 0;

  const helpsSince = now - duration({ days: 7 });

  Object.values(state.global.members).forEach((member) => {
    Object.values(member.helps).forEach((contribution) => {
      // Bail if contribution is not from this week.
      if (contribution.createdAt < helpsSince) return;

      // Add helps to total.
      totalHelps += 1;
    });
  });

  return totalHelps;
}

/**
 * Check if team feature is unlocked for user.
 * @param state
 */
export function isTeamFeatureUnlocked(state: State): boolean {
  return features(state).teams && state.match2.level >= 20;
}

export type TeamEventStatus =
  | 'in-progress'
  | 'complete-no-contribution'
  | 'complete-rewards-available'
  | 'complete-already-claimed';

/**
 * Get a list of user status per event
 */
export function getTeamEventStatus(args: {
  team: TeamState;
  userId: string;
  eventId: string;
  now: number;
}): TeamEventStatus {
  const event = args.team.global.events[args.eventId];
  if (event?.type === 'chest') {
    return getTeamChestEventStatus(args);
  } else {
    return getTeamCompetitionEventStatus(args);
  }
}

/**
 * Get rewards for a team event.
 */
export function getTeamEventRewards(
  teamState: TeamState,
  teamId: string,
  eventId: string,
  eventType: TeamEventType,
  leaderboard: TeamEventData,
): Match2Reward[] {
  switch (eventType) {
    case 'chest': {
      const seed = eventId + teamId;
      return getTeamChestRewards(seed);
    }
    case 'competition': {
      const position =
        leaderboard?.teams.findIndex((x) => x.id === teamId) ?? -1;
      const rewards = TEAM_COMPETITION_REWARDS[position];
      const contributions = Object.values(teamState.global.members).filter(
        (x) => !!x.contributions[eventId]?.score,
      ).length;
      const totalRewards = rewards?.amount ?? 0;
      const dividedRewards = Math.ceil(totalRewards / contributions);

      return [{ type: 'gems', amount: dividedRewards }];
    }
    default:
      assertNever(eventType);
  }
}
